import { type ChangeEvent, Fragment, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { FileHandler } from '@tiptap/extension-file-handler';
import { Selection } from '@tiptap/pm/state';
import { type Editor, EditorContent, useEditor } from '@tiptap/react';
import { ANNOUNCEMENT_BODY_EXTENSIONS } from '@/app/modules/announcements/core/_consts';
import {
  AnnouncementAlignment,
  type AnnouncementMediaNode,
  AnnouncementMediaSize,
} from '@/app/modules/announcements/core/_models';
import AnnouncementColorMenu from '@/app/pages/notifications/announcements/components/AnnouncementColorMenu';
import AnnouncementMediaUploadList from '@/app/pages/notifications/announcements/components/AnnouncementMediaUploadList';
import {
  alignSelection,
  getFinishedMediaUploads,
  getSelectedMediaType,
  insertYoutubeVideo,
  isAlignedTo,
  toggleLink,
} from '@/app/pages/notifications/announcements/core/_utils';
import {
  type AnnouncementColorOption,
  type AnnouncementMediaEnqueue,
  type AnnouncementMediaUploadEntry,
  type AnnouncementMediaSizeOption,
  type AnnouncementToolbarAction,
} from '@/app/pages/notifications/announcements/core/_models';
import '@/app/modules/announcements/components/AnnouncementRichText.scss';

type AnnouncementRichTextEditorProps = {
  inputId: string;
  label: string;
  value: string;
  onChange: (body: string) => void;
  disabled: boolean;
  uploadEntries: AnnouncementMediaUploadEntry[];
  onMediaSelected: AnnouncementMediaEnqueue;
  onRetryMediaUpload: (id: string) => void;
  onRemoveMediaUpload: (id: string) => void;
};

const TEXT_COLORS: AnnouncementColorOption[] = [
  { label: 'Vermelho', value: '#D92D20' },
  { label: 'Laranja', value: '#E8590C' },
  { label: 'Verde', value: '#2F9E44' },
  { label: 'Azul', value: '#1B84FF' },
  { label: 'Roxo', value: '#7239EA' },
  { label: 'Cinza', value: '#78829D' },
];

const HIGHLIGHT_COLORS: AnnouncementColorOption[] = [
  { label: 'Amarelo', value: '#FFF3A3' },
  { label: 'Verde', value: '#D3F9D8' },
  { label: 'Azul', value: '#D0EBFF' },
  { label: 'Rosa', value: '#FFDEEB' },
  { label: 'Laranja', value: '#FFE8CC' },
];

// Texto e marca-texto guardam listas separadas das últimas cores escolhidas.
const TEXT_CUSTOM_COLORS_KEY = 'easypdv-announcement-colors:v1:text';
const HIGHLIGHT_CUSTOM_COLORS_KEY = 'easypdv-announcement-colors:v1:highlight';

const MEDIA_MIME_TYPES = 'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm';

// Cada grupo é separado por uma barra na toolbar; as cores entram entre a formatação e as listas.
const HEADING_ACTIONS: AnnouncementToolbarAction[] = [
  {
    label: 'Título de seção',
    icon: 'bi-type-h2',
    isActive: (editor) => editor.isActive('heading', { level: 2 }),
    run: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    label: 'Subtítulo de seção',
    icon: 'bi-type-h3',
    isActive: (editor) => editor.isActive('heading', { level: 3 }),
    run: (editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
  },
];

// Com uma mídia selecionada, os mesmos botões alinham a mídia; justificar só faz sentido no texto.
const ALIGN_ACTIONS: AnnouncementToolbarAction[] = [
  {
    label: 'Alinhar à esquerda',
    icon: 'bi-text-left',
    isActive: (editor) => isAlignedTo(editor, AnnouncementAlignment.LEFT),
    run: (editor) => alignSelection(editor, AnnouncementAlignment.LEFT),
  },
  {
    label: 'Centralizar',
    icon: 'bi-text-center',
    isActive: (editor) => isAlignedTo(editor, AnnouncementAlignment.CENTER),
    run: (editor) => alignSelection(editor, AnnouncementAlignment.CENTER),
  },
  {
    label: 'Alinhar à direita',
    icon: 'bi-text-right',
    isActive: (editor) => isAlignedTo(editor, AnnouncementAlignment.RIGHT),
    run: (editor) => alignSelection(editor, AnnouncementAlignment.RIGHT),
  },
  {
    label: 'Justificar',
    icon: 'bi-justify',
    isActive: (editor) => isAlignedTo(editor, AnnouncementAlignment.JUSTIFY),
    run: (editor) => alignSelection(editor, AnnouncementAlignment.JUSTIFY),
    isDisabled: (editor) => !!getSelectedMediaType(editor),
  },
];

const MEDIA_SIZE_OPTIONS: AnnouncementMediaSizeOption[] = [
  { label: 'Largura pequena', text: '25%', size: AnnouncementMediaSize.SMALL },
  { label: 'Largura média', text: '50%', size: AnnouncementMediaSize.MEDIUM },
  { label: 'Largura grande', text: '75%', size: AnnouncementMediaSize.LARGE },
  { label: 'Largura total', text: '100%', size: AnnouncementMediaSize.FULL },
];

const MARK_ACTIONS: AnnouncementToolbarAction[] = [
  {
    label: 'Negrito',
    icon: 'bi-type-bold',
    isActive: (editor) => editor.isActive('bold'),
    run: (editor) => editor.chain().focus().toggleBold().run(),
  },
  {
    label: 'Itálico',
    icon: 'bi-type-italic',
    isActive: (editor) => editor.isActive('italic'),
    run: (editor) => editor.chain().focus().toggleItalic().run(),
  },
  {
    label: 'Sublinhado',
    icon: 'bi-type-underline',
    isActive: (editor) => editor.isActive('underline'),
    run: (editor) => editor.chain().focus().toggleUnderline().run(),
  },
  {
    label: 'Tachado',
    icon: 'bi-type-strikethrough',
    isActive: (editor) => editor.isActive('strike'),
    run: (editor) => editor.chain().focus().toggleStrike().run(),
  },
];

const BLOCK_ACTIONS: AnnouncementToolbarAction[] = [
  {
    label: 'Lista com marcadores',
    icon: 'bi-list-ul',
    isActive: (editor) => editor.isActive('bulletList'),
    run: (editor) => editor.chain().focus().toggleBulletList().run(),
  },
  {
    label: 'Lista numerada',
    icon: 'bi-list-ol',
    isActive: (editor) => editor.isActive('orderedList'),
    run: (editor) => editor.chain().focus().toggleOrderedList().run(),
  },
  {
    label: 'Citação',
    icon: 'bi-quote',
    isActive: (editor) => editor.isActive('blockquote'),
    run: (editor) => editor.chain().focus().toggleBlockquote().run(),
  },
  {
    label: 'Código',
    icon: 'bi-code',
    isActive: (editor) => editor.isActive('code'),
    run: (editor) => editor.chain().focus().toggleCode().run(),
  },
  {
    label: 'Bloco de código',
    icon: 'bi-code-square',
    isActive: (editor) => editor.isActive('codeBlock'),
    run: (editor) => editor.chain().focus().toggleCodeBlock().run(),
  },
  { label: 'Link', icon: 'bi-link-45deg', isActive: (editor) => editor.isActive('link'), run: toggleLink },
  {
    label: 'Vídeo do YouTube',
    icon: 'bi-youtube',
    isActive: (editor) => editor.isActive('youtube'),
    run: insertYoutubeVideo,
  },
];

const ACTION_GROUPS: AnnouncementToolbarAction[][] = [HEADING_ACTIONS, ALIGN_ACTIONS, MARK_ACTIONS];

function AnnouncementRichTextEditor({
  inputId,
  label,
  value,
  onChange,
  disabled,
  uploadEntries,
  onMediaSelected,
  onRetryMediaUpload,
  onRemoveMediaUpload,
}: AnnouncementRichTextEditorProps) {
  const labelId = `${inputId}-label`;
  const fileRef = useRef<HTMLInputElement>(null);
  // Trocar a key recria o input vazio: escolher o mesmo arquivo de novo volta a disparar o onChange.
  const [fileInputKey, setFileInputKey] = useState(0);
  // O FileHandler guarda os callbacks da criação do editor; o ref entrega a fila do render atual.
  const onMediaSelectedRef = useRef(onMediaSelected);
  onMediaSelectedRef.current = onMediaSelected;

  // Colar e escolher usam o fim da seleção: logo depois de inserir, a mídia nova fica selecionada.
  const insertMedia = (target: Editor, files: File[], position: number) => {
    const slots = onMediaSelectedRef.current(files);
    if (slots.length === 0) return;
    const media = slots.map((slot) => ({ type: slot.type, attrs: { src: slot.src, alt: slot.alt } }));
    target.chain().focus().insertContentAt(position, media).run();
  };

  const editor = useEditor({
    extensions: [
      ...ANNOUNCEMENT_BODY_EXTENSIONS,
      FileHandler.configure({
        onPaste: (current, files) => insertMedia(current, files, current.state.selection.to),
        onDrop: (current, files, position) => insertMedia(current, files, position),
      }),
    ],
    content: value,
    editable: !disabled,
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: {
        id: inputId,
        class: 'announcement-rich-text form-control border-0 flex-root w-100 min-h-200px',
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-labelledby': labelId,
      },
    },
    // Vazio vira '' para a validação não confundir o parágrafo vazio do editor com texto.
    onUpdate: ({ editor: current }) => {
      const body = current.isEmpty ? '' : current.getHTML();
      onChange(body);
    },
  });

  // Edição e duplicação chegam depois do primeiro render: o editor recebe o texto sem disparar onChange.
  useEffect(() => {
    const currentBody = editor.isEmpty ? '' : editor.getHTML();
    if (currentBody === value) return;
    const finishedUploads = getFinishedMediaUploads(currentBody, value);
    if (finishedUploads.size > 0) {
      // O fim de um envio só troca a origem da mídia, fora do desfazer: as edições de antes continuam desfazíveis.
      const { tr } = editor.state;
      editor.state.doc.descendants((node, position) => {
        const source: unknown = node.attrs.src;
        if (typeof source !== 'string') return;
        const publicUrl = finishedUploads.get(source);
        if (publicUrl) tr.setNodeAttribute(position, 'src', publicUrl);
      });
      tr.setMeta('addToHistory', false);
      tr.setMeta('preventUpdate', true);
      editor.view.dispatch(tr);
      return;
    }
    // Outro texto chegou de fora: troca o documento fora do desfazer e devolve a seleção, se a posição ainda existe.
    const selection: unknown = editor.state.selection.toJSON();
    editor.chain().setMeta('addToHistory', false).setContent(value, { emitUpdate: false }).run();
    try {
      const restored = Selection.fromJSON(editor.state.doc, selection);
      const selectionTransaction = editor.state.tr.setSelection(restored);
      editor.view.dispatch(selectionTransaction);
    } catch {
      // Outro rascunho chegou e a posição antiga não existe nele: o cursor fica onde o setContent pôs.
    }
  }, [editor, value]);

  // Sem o `false`, travar e destravar o editor dispara onUpdate como se fosse edição.
  useEffect(() => {
    editor.setEditable(!disabled, false);
  }, [editor, disabled]);

  const textColor = editor.getAttributes('textStyle').color as string | undefined;
  const highlightColor = editor.getAttributes('highlight').color as string | undefined;
  const selectedMediaType = getSelectedMediaType(editor);
  const selectedMediaSize = selectedMediaType
    ? (editor.getAttributes(selectedMediaType).size as AnnouncementMediaSize | null)
    : null;

  const resizeSelectedMedia = (mediaType: AnnouncementMediaNode, size: AnnouncementMediaSize) =>
    editor.chain().focus().updateAttributes(mediaType, { size }).run();

  const renderAction = (action: AnnouncementToolbarAction) => {
    const isActive = action.isActive(editor);
    const isDisabled = disabled || !!action.isDisabled?.(editor);
    return (
      <button
        key={action.label}
        aria-label={action.label}
        aria-pressed={isActive}
        className={clsx('btn btn-sm btn-icon btn-active-light-primary', isActive && 'active')}
        disabled={isDisabled}
        title={action.label}
        type="button"
        onClick={() => action.run(editor)}
      >
        <i className={clsx('bi fs-5', action.icon)} />
      </button>
    );
  };

  const openFilePicker = () => fileRef.current?.click();

  const handleFileSelection = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files ?? [];
    const files = Array.from(selectedFiles);
    setFileInputKey((key) => key + 1);
    if (files.length === 0) return;
    insertMedia(editor, files, editor.state.selection.to);
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <span className="form-label required d-block" id={labelId}>
        {label}
      </span>
      <div className="announcement-editor-frame d-flex flex-column flex-root">
        <div
          aria-label="Formatação"
          className="announcement-editor-toolbar position-sticky d-flex flex-wrap align-items-center bg-gray-100"
          role="toolbar"
        >
          {ACTION_GROUPS.map((group) => (
            <Fragment key={group[0].label}>
              {group.map(renderAction)}
              <span aria-hidden="true" className="announcement-editor-separator w-1px h-20px bg-gray-300" />
            </Fragment>
          ))}
          <AnnouncementColorMenu
            activeColor={textColor}
            colors={TEXT_COLORS}
            customColorsKey={TEXT_CUSTOM_COLORS_KEY}
            disabled={disabled}
            icon="bi-fonts"
            id={`${inputId}-text-color`}
            label="Cor do texto"
            onClear={() => editor.chain().focus().unsetColor().run()}
            onSelect={(color) => editor.chain().focus().setColor(color).run()}
          />
          <AnnouncementColorMenu
            activeColor={highlightColor}
            colors={HIGHLIGHT_COLORS}
            customColorsKey={HIGHLIGHT_CUSTOM_COLORS_KEY}
            disabled={disabled}
            icon="bi-highlighter"
            id={`${inputId}-highlight`}
            label="Marca-texto"
            onClear={() => editor.chain().focus().unsetHighlight().run()}
            onSelect={(color) => editor.chain().focus().setHighlight({ color }).run()}
          />
          <span aria-hidden="true" className="announcement-editor-separator w-1px h-20px bg-gray-300" />
          {BLOCK_ACTIONS.map(renderAction)}
          {selectedMediaType ? (
            <>
              <span aria-hidden="true" className="announcement-editor-separator w-1px h-20px bg-gray-300" />
              {MEDIA_SIZE_OPTIONS.map((option) => {
                const isSelectedSize = selectedMediaSize === option.size;
                return (
                  <button
                    key={option.size}
                    aria-label={option.label}
                    aria-pressed={isSelectedSize}
                    className={clsx('btn btn-sm btn-active-light-primary px-2', isSelectedSize && 'active')}
                    disabled={disabled}
                    title={option.label}
                    type="button"
                    onClick={() => resizeSelectedMedia(selectedMediaType, option.size)}
                  >
                    {option.text}
                  </button>
                );
              })}
            </>
          ) : null}
        </div>
        <EditorContent className="d-flex flex-root" editor={editor} />
      </div>
      <div className="d-flex align-items-center flex-wrap gap-3 mt-3">
        <button className="btn btn-sm btn-light" disabled={disabled} type="button" onClick={openFilePicker}>
          Adicionar imagem ou vídeo
        </button>
        <span className="text-muted fs-7">
          Cole, arraste ou selecione imagens de até 10 MiB e vídeos MP4 ou WebM de até 20 MiB. Os arquivos enviados são
          públicos. Para um vídeo do YouTube, cole o link numa linha própria ou use o botão do YouTube.
        </span>
        <input
          key={fileInputKey}
          ref={fileRef}
          hidden
          multiple
          accept={MEDIA_MIME_TYPES}
          aria-label="Selecionar imagens ou vídeos"
          disabled={disabled}
          type="file"
          onChange={handleFileSelection}
        />
      </div>
      <AnnouncementMediaUploadList
        entries={uploadEntries}
        onRemove={onRemoveMediaUpload}
        onRetry={onRetryMediaUpload}
      />
    </div>
  );
}

export default AnnouncementRichTextEditor;
